/**
 * Retrieves the current application title from system environment variables.
 * @default 'BCP Wizard Ops Portal'
 */
export const getApplicationTitle = (): string => {
  return import.meta.env.VITE_APP_TITLE || 'BCP Wizard Ops Portal';
};

/**
 * Retrieves the current environment from system environment variables.
 * @default 'local'
 */
export const getCurrentEnvironment = (): string => {
  return import.meta.env.VITE_CUSTOM_ENV || 'local';
};

/**
 * Retrieves the current MUI X license from system environment variables.
 * @default ''
 */
export const getMUIXLicense = (): string => {
  return import.meta.env.VITE_MUI_X_LICENSE || '';
};

/**
 * Retrieves the current application build version from system environment variables.
 * @default '0.0.0'
 */
export const getCurrentApplicationBuildVersion = (): string => {
  const fullVersion = import.meta.env.VITE_BUILD_VERSION || '0.0.0';
  const parts = fullVersion.split('.');
  const firstThree = parts.slice(0, 3);
  return firstThree.join('.');
};

/**
 * Retrieves the API base URL from system environment variables.
 * Uses VITE_API_BASE_URL environment variable for all environments.
 */
export const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || '';
};

// OAuth Configuration

/**
 * Retrieves the OAuth issuer URL from environment variables.
 */
export const getOAuthIssuer = (): string => {
  return import.meta.env.VITE_OAUTH_ISSUER || '';
};

/**
 * Retrieves the OAuth client ID from environment variables.
 */
export const getOAuthClientId = (): string => {
  return import.meta.env.VITE_OAUTH_CLIENT_ID || '';
};

/**
 * Retrieves the OAuth redirect URI from environment variables.
 * Defaults to current origin + /login/callback
 */
export const getOAuthRedirectUri = (): string => {
  return import.meta.env.VITE_OAUTH_REDIRECT_URI || '';
};

/**
 * Check if OAuth is configured
 */
export const isOAuthConfigured = (): boolean => {
  return !!(getOAuthIssuer() && getOAuthClientId());
};

/**
 * Sherlock instance name.
 */
export const getSherlockInstance = (): string => {
  return import.meta.env.VITE_SHERLOCK_INSTANCE || 'igniteops';
};

/** Fallback when `VITE_SERVICENOW_PORTAL_BASE_URL` is not set at build time. */
const DEFAULT_SERVICENOW_PORTAL_ORIGIN = 'https://globalservicesportal.genesys.com';

export function resolveIncidentSysIdFromRecord(row: Record<string, unknown>): string | undefined {
  const raw = row.sysId ?? row.sys_id;
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s.length > 0 ? s : undefined;
}

/**
 * Direct link to incident form (`incident.do?sys_id=...`) on Genesys Global Services Portal.
 * Overrides base with `VITE_SERVICENOW_PORTAL_BASE_URL` when set (no trailing slash).
 */
export function getServiceNowIncidentPortalUrl(sysId: string | undefined): string | null {
  const fromEnv = import.meta.env.VITE_SERVICENOW_PORTAL_BASE_URL?.trim().replace(/\/?$/, '');
  const base = (fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_SERVICENOW_PORTAL_ORIGIN).replace(
    /\/?$/,
    '',
  );
  if (!sysId || typeof sysId !== 'string' || !sysId.trim()) return null;
  return `${base}/incident.do?sys_id=${encodeURIComponent(sysId.trim())}`;
}

export function getServiceNowIncidentPortalUrlFromTicketRow(row: Record<string, unknown>): string | null {
  const id = resolveIncidentSysIdFromRecord(row);
  return id != null ? getServiceNowIncidentPortalUrl(id) : null;
}

